<template>
    <ListFrame
        class="articles-list-frame"
        :no-results="resultListEmpty"
        :loading="initialAssetsLoading"
        :error="error"
        :small-content-container="false"
        large-content-container
        :total="total"
    >
        <template #filter="{ isMobile }">
            <ListFilterUrlSync
                v-model:selected="selected"
                sort-save-key="article_sort"
                :fetch-aggregation="fetchAggregation"
                :facet-collection-id="FacetCollection.articleSelection"
                :is-mobile-view="isMobile"
                :more-menu-entries="$q.screen.gt.xs ? [] : menuEntries"
                :sort-order-entries="sortOrderItems"
                :hide-facets="!!blobId"
                @on-sort-selection-changed="onChangeSortOrder"
                @on-open-filter="onOpenFilterHandler"
            />
        </template>
        <template #banner v-if="showBannerTop">
            <SearchBanner
                :search-result-status="resultListEmpty ? 'none' : 'tooMany'"
                :context="context"
                @on-open-need-qualification="$emit('onOpenNeedQualification')"
                hide-ai-assistant
            />
        </template>
        <template #content>
            <Table
                data-qs="articles-list-table"
                :q-data-source="qDataSource"
                :data-loading="loading"
                :has-more-results="hasMoreResults"
                :custom-sticky-right-cell-column-name="ADD_TO_COLLECTION_COL_NAME"
                :custom-cells="[QIT_PREVIEW_IMAGE_COLUMN]"
                :mobile-action-column-name="ADD_TO_COLLECTION_COL_NAME"
                :link-to="(row) => routeToCommonArticle(row.id)"
                sticky-last-column
                @on-load-more="loadMoreArticles()"
            >
                <template #custom-cell-column-name="{ row }">
                    <div class="row no-wrap items-center col-gap-m">
                        <ContextButtons
                            size="sm"
                            class="buttons"
                            :article-id="row.id"
                            :view="GuiElementView.article_selection"
                            :flat="false"
                        />
                        <AddToCollectionButton
                            size="sm"
                            color="secondary"
                            class="buttons"
                            :data-qs="`add-article-to-collection-button-${row.id}`"
                            @click="openCollectionOverlay(row)"
                        />
                    </div>
                </template>
                <template #mobile-action="{ row }">
                    <div class="row no-wrap items-center col-gap-m">
                        <ContextButtons
                            size="sm"
                            class="buttons"
                            :article-id="row.id"
                            :view="GuiElementView.article_selection"
                            :flat="false"
                        />
                        <AddToCollectionButton
                            size="sm"
                            color="secondary"
                            class="buttons"
                            :data-qs="`add-article-to-collection-button-${row.id}`"
                            @click="openCollectionOverlay(row)"
                        />
                    </div>
                </template>
            </Table>
            <div v-if="tooFewResults">
                <SearchBanner
                    class="q-mt-md"
                    search-result-status="tooFew"
                    :context="context"
                    @on-open-need-qualification="$emit('onOpenNeedQualification')"
                    hide-ai-assistant
                />
            </div>
        </template>
        <template #no-results>
            <template v-if="blobId">
                <NoResultsError />
            </template>
            <template v-else>
                <NoSearchResultsError :search-tab="$t('mechanic.Articles')" :class="{ 'q-mx-lg': $q.screen.lt.sm }" />
            </template>
        </template>
    </ListFrame>
    <CollectionArticleOverlay
        v-if="articleModelOverlay"
        :article="articleModelOverlay"
        @on-close="closeCollectionsOverlay"
        @on-save-succeeded="closeCollectionsOverlay"
    />
</template>

<script setup lang="ts">
/* Komponente, die eine Liste von Artikeln ohne Einbauort anzeigt. */
import CollectionArticleOverlay, {
    ArticleModelOverlay,
} from "@/abilities/collections/components/overlay/collection-article-overlay.vue";
import {
    ADD_TO_COLLECTION_COL_NAME,
    useArticlesDataSource,
} from "@/abilities/mechanic/pages/article-search/articles-list-data-source";
import NoResultsError from "@/abilities/mechanic/pages/components/no-results-error.vue";
import { AccessFeature } from "@/shared/access-control/access-control.ts";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import AddToCollectionButton from "@/shared/collections/add-to-collection-button.vue";
import { QIT_PREVIEW_IMAGE_COLUMN, QTableRowType, resolveCellText } from "@/shared/components/table/q-data-source";
import Table from "@/shared/components/table/table.vue";
import { useAbilityRoute } from "@/shared/environment/ability-route";
import { CommonRouteAliases, CommonSearchTabAliases } from "@/shared/environment/common-route-aliases";
import { useSelectedFacets } from "@/shared/facets/composables/selected-facets";
import { Facet, FacetCollection } from "@/shared/facets/facets.model";
import ListFilterUrlSync from "@/shared/list-filter/list-filter-renderer-url-sync.vue";
import ListFrame from "@/shared/list-frame/list-frame.vue";
import { useHeaderMenuEntries } from "@/shared/search/header-menu-entries.ts";
import NoSearchResultsError from "@/shared/search/no-search-results-error.vue";
import SearchBanner from "@/shared/search/search-banner.vue";
import { TOO_FEW_RESULTS_THRESHOLD, TOO_MANY_RESULTS_THRESHOLD } from "@/shared/search/search.constants.ts";
import { computed, ref, toRefs, watch } from "vue";
import { useArticlesListSort } from "./articles-list.model";
import { useMatomo } from "@/shared/analytics/matomo.ts";
import ContextButtons from "@/shared/ui-extensions/components/context-buttons.vue";
import { GuiElementView } from "@/shared/ui-extensions/context.types.ts";

const { trackSearch } = useMatomo();

const props = defineProps<{
    fullTextSearchValue: string;
    blobId?: string;
    isSearch?: boolean;
}>();

defineEmits<{ onOpenNeedQualification: [] }>();

const { blobId } = toRefs(props);
const facet = ref<Facet | undefined>();
const articleModelOverlay = ref<ArticleModelOverlay | undefined>();

const { menuEntries } = useHeaderMenuEntries(computed(() => CommonSearchTabAliases.commonArticles));
const { getPathToChildWithoutParents } = useAbilityRoute();
const { sorting, onChangeSortOrder, selected } = useSelectedFacets();
const { hasAccess: hasProducts } = useHasAccess({ featureID: AccessFeature.products });

const context = computed(() => {
    return { text: props.fullTextSearchValue };
});
const fulltext = computed(() => props.fullTextSearchValue);

const {
    qDataSource,
    loading,
    error,
    resultListEmpty,
    hasMoreResults,
    loadMoreArticles,
    total,
    fetchAggregation,
    articlesResult,
} = useArticlesDataSource(context, selected, sorting, facet, blobId);

const initialAssetsLoading = computed(() => {
    return !articlesResult.value;
});

/**
 * Returns Route to Article Detail Page without Context (Einbauort)
 */
function routeToCommonArticle(articleNodeId: string) {
    return getPathToChildWithoutParents(CommonRouteAliases.commonArticle, {
        [CommonRouteAliases.commonArticle]: articleNodeId,
    });
}

const { sortOrderItems } = useArticlesListSort();

const onOpenFilterHandler = (facetProp: Facet) => {
    facet.value = facetProp;
};

const openCollectionOverlay = (cell: QTableRowType) => {
    if (articleModelOverlay.value?.articleId === cell.id) {
        closeCollectionsOverlay();
        return;
    }

    articleModelOverlay.value = { articleId: cell.id, title: resolveCellText(cell.title) || cell.id };
};

const closeCollectionsOverlay = () => {
    articleModelOverlay.value = undefined;
};

const isBannerAllowed = computed(() => {
    if (!hasProducts.value) return false;
    return !initialAssetsLoading.value && props.isSearch;
});

const showBannerTop = computed(() => {
    if (!isBannerAllowed.value) return false;
    if (blobId.value && resultListEmpty.value) return false;

    return (articlesResult.value && total.value?.count > TOO_MANY_RESULTS_THRESHOLD) || resultListEmpty.value;
});

const tooFewResults = computed(() => {
    if (!isBannerAllowed.value || blobId.value) return false;

    return articlesResult.value && total.value?.count <= TOO_FEW_RESULTS_THRESHOLD;
});

watch(articlesResult, () => {
    if (articlesResult.value && fulltext.value) {
        trackSearch(fulltext.value, "articles", total.value?.count);
    }
});
</script>

<style lang="scss" scoped>
:deep(.buttons) {
    max-height: 30px;
}

:deep(.table-container) {
    overflow: hidden;
}

.articles-list-frame {
    :deep(.slot-content) {
        display: grid;
        overflow: hidden;

        .screen--xs & {
            grid-template-rows: 1fr auto;
        }
    }
}
</style>
/
